/** The names and phrases engine, api and web share (OR-32.1, OR-32.4). Plain strings, no @bsim imports. */

/** The one player-facing name per KPI key. */
export const KPI_NAME: Readonly<Record<string, string>> = {
  cet1Marked: "Marked CET1",
  cet1Reported: "Reported CET1",
  creToTrbc: "Commercial property loans to risk-based capital",
};

/** What commercial property loans are measured against. */
export const CRE_DENOMINATOR = "risk-based capital";

/** First-use definition of Marked CET1. The glossary `cet1-marked` plain text equals it verbatim (scripts/calib/build_glossary_v1.py). */
export const MARKED_CET1_DEFINITION =
  "The bank's loss cushion (CET1) after also counting the price losses on all its bonds, including those it plans to keep until they are repaid.";

/** Value-basis phrases; each carries its horizon. */
export const VALUE_BASIS_PHRASE: Readonly<{ alone: string; inPlan: string; price: string; counted: string }> = {
  alone: "over three years, priced alone on the forecast path",
  inPlan: "over three years, in the whole plan",
  price: "year-end share price only",
  counted: "counted on the year as played",
};

/** The one phrase for where a limit-tested measure is read (OR-30.83): equity value and liquidity coverage are tested at the worst quarter-end of the year, every other measure at year-end. Engine and web share it. */
export const READING_PHRASE = { worstQuarterEnd: "at its worst quarter-end", yearEnd: "at year-end" } as const;

/** The miss of the "no change" forecast, named the same way everywhere. */
export const sameAsLastYearMiss = (bpText: string): string => `“Same as last year” would have been off by ${bpText}`;

/**
 * The rule phrases every screen, memo and glossary entry states the same way (JF5). Each is one plain string literal on its own `export const`, so the glossary generator
 * (scripts/calib/build_glossary_v1.py) reads the text from here and the glossary plain text equals it. Numbers that come from content parameters are never typed here: see `managementRule`.
 */

/** What a breach is. */
export const BREACH_DEFINITION = "A quarter-end reading outside a limit you set for your own bank.";

/** What a breach costs at the finale. */
export const RISK_STEWARD_RULE = "Any breach rules you out of the Risk Steward award at the finale.";

/** The Risk Steward award, defined. */
export const RISK_STEWARD_DEFINITION = "A finale award for a bank that never breached its own limits. Any breach rules a bank out.";

/** The Management rating, defined; one part of the overall rating. */
export const MANAGEMENT_DEFINITION = "How well the regulator thinks you run the bank, one part of its overall 1-to-5 rating. It falls when you repeatedly breach your own limits or leave examiner findings open.";

/** The low-rating rank rule (scoring `gateRankedLast`: composite 3 or worse), the one sentence every screen, memo and glossary entry states. */
export const RANK_RULE = "A bank the regulator rates 3 or worse overall (1 is best, 5 is worst) ranks below every bank rated 1 or 2, whatever its score.";

/** The score baseline: the team's own bank, run on today's settings since the start. */
export const TWIN_DEFINITION = "A copy of your bank that keeps today's settings all game.";

/** The forecast and preview baseline: this year only. "Standing policy" names the settings sheet, never a bank. */
export const NO_CHANGE_DEFINITION = "Your bank as it stands today, run for one year with no new decisions.";

/** Who checks the limits at each quarter-end: the risk officer when the game has that seat, otherwise the game itself. */
export const limitChecker = (hasRiskOfficer: boolean): string => (hasRiskOfficer ? "your risk officer" : "the game");

/** The limits notice's opening line. */
export const limitCheckLine = (checker: string): string => `At every quarter-end ${checker} checks your bank against each limit; a reading outside a limit is a breach.`;

/** When a run of breaches lowers Management; the three numbers come from the content parameters (`supervisory.modifiers.mBreach`). */
export const managementRule = (rule: { readonly quarterEnds: number; readonly lookbackQuarterEnds: number; readonly limits: number }): string =>
  `Breaches at ${rule.quarterEnds} of the last ${rule.lookbackQuarterEnds} quarter-ends, or ${rule.limits} limits breached at once, lower the regulator's Management rating, one part of its overall 1-to-5 rating.`;

/** The status line under the rules, once the team has history. */
export const limitStatusLine = (breachCount: number, overallRating: number): string =>
  `So far: ${breachCount === 0 ? "no breaches" : `${breachCount} ${breachCount === 1 ? "breach" : "breaches"}`}; the regulator rates you ${overallRating} overall.`;
